import type {
  CleaningJob,
  PortfolioCalendar,
  PropertyCalendarBlock,
  PropertyCalendarDeposit,
  PropertyCalendarLayers,
  PropertyCalendarOrphanGap,
  PropertyCalendarReview,
  PropertyCalendarStay,
  PropertyCalendarSupply,
} from '@/api/client'
import { snapshotCaveats } from '@/lib/gap-events'
import { buildSupplyMarkers, type SupplyMarker } from '@/lib/supply-events'
import { dayNumber, windowStartDay, WINDOW_DAYS } from './windows'

/**
 * Merging the windows the grid has read into one portfolio (CAP-1416).
 *
 * An item that spans windows (a season-long owner block, a stay over a window
 * boundary) comes back in every window it touches, each time with its real
 * dates (admin-api CAP-1414). Merged by id per layer, it is one item, so it
 * draws as one bar with one sticky label and no seam at a window boundary.
 *
 * Windows are not all equally fresh: the ones on screen refetch on focus and
 * after a block is made or released, while one far off the screen keeps what
 * it read until it is wanted again. So:
 * - where two windows disagree about an item, the one read last wins;
 * - an item a window read later no longer has, although its dates fall in that
 *   window, is gone (a released block): an older window still holding it does
 *   not bring it back.
 *
 * Known trade-off: an item shortened out of a window (a stay cut from 5-25 Nov
 * to 5-10 Nov) is dropped while that window has answered and the one still
 * holding it has not, since its old dates say the newer window should list it.
 * It comes back when that window answers; if that read fails, the grid says it
 * may be out of date.
 */

/** One window's read, as the merge needs it. */
export interface PortfolioWindowRead {
  /** The window's index (see `windows.ts`). */
  window: number
  data: PortfolioCalendar
  /** When it was read (TanStack's `dataUpdatedAt`): later reads win. */
  updatedAt: number
}

/** A layer across every window read: the items, and whether any window could not read it. */
export interface MergedLayer<T> {
  items: T[]
  /** The message of a window that could not read this layer, or null when every one could. */
  error: string | null
  /**
   * Why the answer is not a clean one, in the property tab's sentences: a
   * stale or cut-short orphan-gap snapshot. Empty for every other layer.
   */
  caveats: string[]
}

/** The days an item holds as day numbers: first day, first free day. Null where the API's listing rule is not a date rule. */
interface HeldDays {
  start: number | null
  end: number | null
}

const NO_DAYS: HeldDays = { start: null, end: null }

type OkLayer = Extract<PropertyCalendarLayers[keyof MergedPortfolioLayers], { status: 'ok' }>

/**
 * How each layer is merged: what one window's items are as the merge keeps
 * them, an item's identity, and the days it holds (first day, first free day),
 * which say which windows must list it.
 *
 * `days` is given only where admin-api lists an item by its own days: stays and
 * blocks by their nights, cleanings by their day, gaps by their nights, supply
 * events by their day. Deposits and reviews are listed for a stay that touches
 * the range OR for any of their days in it, which the item alone cannot tell,
 * so they are never taken for released (an older window's copy stays until no
 * read lists it).
 */
interface LayerMerge<Raw, T> {
  /** One window's items as kept: the items themselves, or (supplies) what they put on days. */
  expand?: (items: Raw[]) => T[]
  /** What a read of the layer says about itself (the gaps' snapshot): the latest read's is kept. */
  caveats?: (layer: OkLayer, timeZone: string) => string[]
  key: (item: T) => string
  days: (item: T) => HeldDays
}

const STAYS: LayerMerge<PropertyCalendarStay, PropertyCalendarStay> = {
  key: stay => stay.id,
  days: stay => ({ start: dayNumber(stay.check_in), end: dayNumber(stay.check_out) }),
}

const BLOCKS: LayerMerge<PropertyCalendarBlock, PropertyCalendarBlock> = {
  key: block => `${block.kind}-${block.id}`,
  days: block => ({ start: dayNumber(block.start), end: dayNumber(block.end_exclusive) }),
}

/** admin-api lists a job by its `local_date` (CAP-1414, the jobs list's own filter). */
const CLEANING: LayerMerge<CleaningJob, CleaningJob> = {
  key: job => job.id,
  days: job => {
    const day = dayNumber(job.local_date)
    return day === null ? NO_DAYS : { start: day, end: day + 1 }
  },
}

/** A stay's whole deposit timeline comes back in every window that lists it: one per stay. */
const DEPOSITS: LayerMerge<PropertyCalendarDeposit, PropertyCalendarDeposit> = {
  key: deposit => deposit.reservation_id,
  days: () => NO_DAYS,
}

/** A stay's review window, the same in every window that lists it: one per stay. */
const REVIEWS: LayerMerge<PropertyCalendarReview, PropertyCalendarReview> = {
  key: review => review.reservation_id,
  days: () => NO_DAYS,
}

/** The sentences one read's snapshot carries, made once per read (they format a time). */
const gapCaveats = new WeakMap<object, string[]>()

/**
 * A gap is returned whole by every window it touches. Gaps on one property
 * never overlap. Every window reads the same stored snapshot, so the latest
 * read says how old and how complete it is.
 */
const ORPHAN_GAPS: LayerMerge<PropertyCalendarOrphanGap, PropertyCalendarOrphanGap> = {
  caveats: (layer, timeZone) => {
    let caveats = gapCaveats.get(layer)
    if (!caveats) {
      caveats = 'stale' in layer ? snapshotCaveats(layer, timeZone) : []
      gapCaveats.set(layer, caveats)
    }
    return caveats
  },
  key: gap => gap.gap_key || gap.first_night,
  days: gap => {
    const last = dayNumber(gap.last_night)
    return { start: dayNumber(gap.first_night), end: last === null ? null : last + 1 }
  },
}

/**
 * A supply category is NOT the same item in every window: its runout counts
 * only in the window whose range holds it (`runout_in_range`), and it lists
 * the deliveries in that window's range alone. Merged by category, the window
 * read last would wipe the others' deliveries. So each window's categories are
 * turned into the events they put on days (the property tab's own
 * `buildSupplyMarkers`), and those are merged, by category, kind and day.
 */
const expandedSupplies = new WeakMap<PropertyCalendarSupply[], SupplyMarker[]>()

const SUPPLIES: LayerMerge<PropertyCalendarSupply, SupplyMarker> = {
  expand: items => {
    // The same read is merged again whenever another window answers: the
    // same events, so the layout's per-item cache still holds for them.
    let markers = expandedSupplies.get(items)
    if (!markers) {
      markers = buildSupplyMarkers(items, () => true)
      expandedSupplies.set(items, markers)
    }
    return markers
  },
  key: marker => marker.key,
  days: marker => {
    const day = dayNumber(marker.day)
    return day === null ? NO_DAYS : { start: day, end: day + 1 }
  },
}

export interface MergedPortfolioLayers {
  stays: MergedLayer<PropertyCalendarStay>
  blocks: MergedLayer<PropertyCalendarBlock>
  cleaning: MergedLayer<CleaningJob>
  deposits: MergedLayer<PropertyCalendarDeposit>
  /** The runouts and deliveries the windows put on days, not the categories. */
  supplies: MergedLayer<SupplyMarker>
  reviews: MergedLayer<PropertyCalendarReview>
  orphan_gaps: MergedLayer<PropertyCalendarOrphanGap>
}

/** A property's row across every window read. */
export interface PortfolioProperty {
  property_id: string
  name: string
  thumbnail_url: string | null
  time_zone: string
  /**
   * When the latest read was made (its `generated_at`): the server's "now",
   * which says whether a review window is still open. Never the browser's clock.
   */
  generated_at: string | null
  layers: MergedPortfolioLayers
}

interface Seen<T> {
  item: T
  updatedAt: number
}

interface LayerAccumulator<T> {
  items: Map<string, Seen<T>>
  /** Per window read, the keys it listed: to tell "released" from "not in this window". */
  keysByRead: Map<PortfolioWindowRead, Set<string>>
  error: { message: string; updatedAt: number } | null
  caveats: { list: string[]; updatedAt: number } | null
}

function newLayer<T>(): LayerAccumulator<T> {
  return { items: new Map(), keysByRead: new Map(), error: null, caveats: null }
}

type LayerId = keyof MergedPortfolioLayers

function addLayer<Raw, T>(
  accumulator: LayerAccumulator<T>,
  layer: PropertyCalendarLayers[LayerId],
  read: PortfolioWindowRead,
  merge: LayerMerge<Raw, T>,
  timeZone: string
) {
  if (layer.status !== 'ok') {
    if (!accumulator.error || read.updatedAt > accumulator.error.updatedAt) {
      accumulator.error = { message: layer.message, updatedAt: read.updatedAt }
    }
    return
  }
  if (merge.caveats && (!accumulator.caveats || read.updatedAt > accumulator.caveats.updatedAt)) {
    accumulator.caveats = { list: merge.caveats(layer, timeZone), updatedAt: read.updatedAt }
  }
  const raw = layer.items as Raw[]
  const items = merge.expand ? merge.expand(raw) : (raw as unknown as T[])
  const keys = new Set<string>()
  for (const item of items) {
    const key = merge.key(item)
    keys.add(key)
    const seen = accumulator.items.get(key)
    // Map keeps an item where it was first met, so the order (and with it how
    // ties stack into lanes) does not depend on which window answered first.
    if (!seen || read.updatedAt > seen.updatedAt) {
      accumulator.items.set(key, { item, updatedAt: read.updatedAt })
    }
  }
  accumulator.keysByRead.set(read, keys)
}

/** Whether a window must list an item holding these days: the API's own rule (`check_out > from && check_in <= to`). */
function windowHolds(window: number, start: number, end: number): boolean {
  const first = windowStartDay(window)
  return end > first && start <= first + WINDOW_DAYS - 1
}

function finishLayer<Raw, T>(
  accumulator: LayerAccumulator<T>,
  merge: LayerMerge<Raw, T>
): MergedLayer<T> {
  const items: T[] = []
  const reads = [...accumulator.keysByRead]
  for (const [key, { item, updatedAt }] of accumulator.items) {
    const { start, end } = merge.days(item)
    const released =
      start !== null &&
      end !== null &&
      reads.some(
        ([read, keys]) =>
          read.updatedAt > updatedAt && windowHolds(read.window, start, end) && !keys.has(key)
      )
    if (!released) items.push(item)
  }
  return {
    items,
    error: accumulator.error?.message ?? null,
    caveats: accumulator.caveats?.list ?? [],
  }
}

/** Every merged layer and how it merges, in one table. */
const MERGES = {
  stays: STAYS,
  blocks: BLOCKS,
  cleaning: CLEANING,
  deposits: DEPOSITS,
  supplies: SUPPLIES,
  reviews: REVIEWS,
  orphan_gaps: ORPHAN_GAPS,
} as const

const LAYER_IDS = Object.keys(MERGES) as LayerId[]

type Accumulators = { [K in LayerId]: LayerAccumulator<MergedPortfolioLayers[K]['items'][number]> }

function newAccumulators(): Accumulators {
  return {
    stays: newLayer(),
    blocks: newLayer(),
    cleaning: newLayer(),
    deposits: newLayer(),
    supplies: newLayer(),
    reviews: newLayer(),
    orphan_gaps: newLayer(),
  }
}

interface PropertyAccumulator {
  header: Omit<PortfolioProperty, 'layers'>
  updatedAt: number
  layers: Accumulators
}

/**
 * Every property across `reads`, which come in the grid's day order. A
 * property's name and photo come from the latest read; the rows keep the order
 * the API gives them, a property only some windows list (made active part-way)
 * coming after the others.
 */
export function mergePortfolioWindows(reads: readonly PortfolioWindowRead[]): PortfolioProperty[] {
  // The latest read sets the row order.
  const latest = reads.reduce<PortfolioWindowRead | null>(
    (best, read) => (!best || read.updatedAt > best.updatedAt ? read : best),
    null
  )
  const properties = new Map<string, PropertyAccumulator>()
  for (const property of latest?.data.properties ?? []) {
    properties.set(property.property_id, {
      header: headerOf(property, latest!.data),
      updatedAt: -Infinity,
      layers: newAccumulators(),
    })
  }

  for (const read of reads) {
    for (const property of read.data.properties) {
      let accumulator = properties.get(property.property_id)
      if (!accumulator) {
        accumulator = {
          header: headerOf(property, read.data),
          updatedAt: -Infinity,
          layers: newAccumulators(),
        }
        properties.set(property.property_id, accumulator)
      }
      if (read.updatedAt > accumulator.updatedAt) {
        accumulator.header = headerOf(property, read.data)
        accumulator.updatedAt = read.updatedAt
      }
      for (const id of LAYER_IDS) {
        addLayer(
          accumulator.layers[id] as LayerAccumulator<unknown>,
          property.layers[id],
          read,
          MERGES[id] as LayerMerge<unknown, unknown>,
          property.time_zone
        )
      }
    }
  }

  return [...properties.values()].map(({ header, layers }) => {
    const finish = <K extends LayerId>(id: K) =>
      finishLayer(
        layers[id] as LayerAccumulator<unknown>,
        MERGES[id] as LayerMerge<unknown, unknown>
      ) as MergedPortfolioLayers[K]
    return {
      ...header,
      layers: {
        stays: finish('stays'),
        blocks: finish('blocks'),
        cleaning: finish('cleaning'),
        deposits: finish('deposits'),
        supplies: finish('supplies'),
        reviews: finish('reviews'),
        orphan_gaps: finish('orphan_gaps'),
      },
    }
  })
}

function headerOf(
  property: PortfolioCalendar['properties'][number],
  data: PortfolioCalendar
): Omit<PortfolioProperty, 'layers'> {
  return {
    property_id: property.property_id,
    name: property.name,
    thumbnail_url: property.thumbnail_url,
    time_zone: property.time_zone,
    generated_at: data.generated_at,
  }
}

/** A property's row with nothing on it: what is known of it before its days are read. */
export function withoutItems(property: PortfolioProperty): PortfolioProperty {
  const empty = { items: [], error: null, caveats: [] }
  return {
    ...property,
    layers: {
      stays: empty,
      blocks: empty,
      cleaning: empty,
      deposits: empty,
      supplies: empty,
      reviews: empty,
      orphan_gaps: empty,
    },
  }
}
